import React, { useEffect, useState } from 'react';
import { createRoot } from 'react-dom/client';
import { Provider } from 'react-redux';
import { store } from '@redux/store';
import { useSelector, useDispatch } from 'react-redux';
// i18n
import '../../i18n';
// styles
import './style.scss';
// TS types
import { TTripType, IThsrTripInfo } from '@redux/common';
import { ITHSRStore } from '@redux/THSR/interfaces';
// hooks
import useLang from 'hooks/useLang';
// Redux
import { RootState } from '@redux/store';
// api
import getEnvConfig from '@api/config';
//Loading component
import Loading from '@component/Common/loading';
import LoadingGradient from '@component/Common/loadingGradient';
// icon component
import LoadingIcon from '@svg/LoadingIcon';
// 高鐵聯票panel
import NormalRailPanel from '@component/THSR';
// 產品清單panel
import ListPanel from '@component/Normal';
import TListPanel from '@component/NormalTHSR';
import GdsListPanel from '@component/GDS';
import TGdsListPanel from '@component/GDSTHSR';
// common function
import { setTHSRInit, getTHSRStations } from '@libs/apiCall';
import { getWindowCustomFunctions, changeLang, isGDS, getTHSRTripInfo } from '@libs/core';
// plugin
import { useTranslation } from 'react-i18next';
// redux actions
import {
    setGListData,
    setListData,
    setIsErpOrder,
    setETID,
    setForeign,
    setLanguage,
    setIsLogin,
    setIsDisney,
    // setDetailFetchData,
    // setIsFailfetchNormal,
    setIsGDS,
    setListStatus,
    setCurrency,
    setProductDescKey
} from '@redux/Common/actions';

export interface WindowCustomData {
    ETID?: string;
    isThsr?: boolean;
    isLogin?: boolean;
    isDisney?: boolean;
    isErpOrdr?: boolean;
    formatPriceByCulture?: (currency: string, price: number, fractionDigits?: number) => number;
}

declare global {
    interface Window extends WindowCustomData {}
}

// 抓取 url params
// Foreign
const getUrlParams = () => {
    const searchUrl: any = new URL(window.location.href);
    let data = [];
    for (let [key, value] of searchUrl.searchParams.entries()) {
        data.push({
            key: key.toString(),
            value: value.toString(),
        });
    }
    return data;
};
// productCode
// 原擷取字串取ETID的方法
const getProductIDParams = () => {
    const urlPathStr: string = window.location.pathname;
    const urlSearchStr: string = window.location.search;
    if (
        // udeveloper.liontravel.com:xxxx 是IT本地開發環境 用hostname屬性不取port
        window.location.hostname === 'uactivity.liontravel.com' ||
        window.location.hostname === 'activity.liontravel.com' ||
        window.location.hostname === 'udeveloper.liontravel.com'
    ) {
        // /detail/185001-台南赤崁樓門票且樂天搜尋
        const dashPosition: number = urlPathStr.indexOf('-');
        if (urlPathStr.indexOf('detail') < 0) return;
        return urlPathStr.slice(8, dashPosition);
    } else {
        // 本機環境 用search屬性抓值
        // ?productCode=185001-台南赤崁樓門票且樂天搜尋
        const dashPosition: number = urlSearchStr.indexOf('-');
        return urlSearchStr.slice(13, dashPosition);
    }
};
// 從URL抓取指定參數
const getUrlValueOfKey = (
    urlData: {
        key: string;
        value: string;
    }[],
    filterKey: string
) => {
    if (urlData.length === 0) return;
    const [matchObj] = urlData.filter((item) => item.key === filterKey);
    if (!matchObj) return; // undefined
    return matchObj.value;
};

// 確認是否為無方案產品(目前只有自建會有無方案 => FormatID == "Default")
const checkDefaultID = (arr) => {
    // 若為Default則只有一個面板，輸出一個給"確認面板現在的項目數"使用
    if (arr.find((child) => child.FormatID === 'Default')) return [arr[0]];
    return arr;
};

// const ETID = getUrlValueOfKey(getUrlParams(), 'productCode');
const URLFOREIGNCapital = getUrlValueOfKey(getUrlParams(), 'Foreign');
const URLFOREIGNLowercase = getUrlValueOfKey(getUrlParams(), 'foreign');
const URLFOREIGN = URLFOREIGNCapital || URLFOREIGNLowercase; // F大小寫都接受

const Detail: React.FC = () => {
    const dispatch = useDispatch();
    // IT在全域中提供的變數與方法
    const windowObj: WindowCustomData = getWindowCustomFunctions();
    const { ETID, isThsr: hasThsr, isLogin, isDisney, isErpOrdr } = windowObj;
    // console.log("ETID",ETID)
    const isGDSURL = ETID && isGDS(ETID);
    const isNormalURL = ETID && !isGDS(ETID);
    const { t, i18n } = useTranslation();

    const { GListData, ListData } = useSelector((state: RootState) => state.Common);
    const { THSROpenedPanel, THSRInitData, THSRIsInitFail }: ITHSRStore = useSelector(
        (state: RootState) => state.THSR
    );

    const [isFailFetchList, setIsFailFetchList] = useState<Boolean>(false);
    const [isFailFetchGDSList, setIsFailFetchGDSList] = useState<Boolean>(false);
    // THSR
    const [thsrOneWayTripInfo, setThsrOneWayTripInfo] = useState<IThsrTripInfo>(null);
    const [thsrRoundTripInfo, setThsrRoundTripInfo] = useState<IThsrTripInfo>(null);
    // 打語系API取得當前語系&幣別
    const multiLangUrl = getEnvConfig().COMMON.multilang;
    const langObj = useLang(multiLangUrl);

    const [isCrossell, setIsCrossell] = useState<boolean | null>(null);
    useEffect(() => {
        const element = document.getElementById('detailOptions');
        console.log('element', element);
        if (element) {
            const etktInfo = element.getAttribute('data-etktinfo');
            if (etktInfo) {
                try {
                    const parsedInfo = JSON.parse(etktInfo);
                    setIsCrossell(parsedInfo.IsCrossell);
                    dispatch(setProductDescKey(parsedInfo.Key));
                } catch (error) {
                    console.error('Failed to parse data-etktinfo', error);
                }
            }
        }
    }, []);

    // 打list、高鐵 API
    useEffect(() => {
        function getInitListData(isGDS: boolean) {
            const listUrl = isGDS ? getEnvConfig().GDS.list : getEnvConfig().normal.list;
            const config = {
                method: 'GET',
                headers: new Headers({
                    'Content-Type': 'application/json',
                }),
            };
            const source = listUrl + `?ETID=${ETID}`;
            try {
                fetch(source, config)
                    .then((res) => res.json())
                    .catch((error) => {
                        isGDS ? setIsFailFetchGDSList(true) : setIsFailFetchList(true);
                        console.error('fetchListDataError:', error);
                    })
                    .then((data) => {
                        console.log('fetchNormalListData', data);
                        isGDS ? dispatch(setGListData(data)) : dispatch(setListData(data));
                    });
            } catch (e) {
                console.log('error msg', e);
            }
        }

        dispatch(setIsErpOrder(isErpOrdr));
        dispatch(setIsLogin(isLogin));
        dispatch(setIsDisney(isDisney));

        //初始化產品list資料
        getInitListData(isGDSURL);

        //初始化高鐵資料
        if (hasThsr) {
            setTHSRInit({ ETID: ETID })(dispatch);
            getTHSRStations()(dispatch);
        }
    }, [dispatch, ETID, isGDSURL, isNormalURL, hasThsr, isDisney, isLogin, isErpOrdr]);

    // 設定語系
    useEffect(() => {
        //將目前語系和幣別設置到redux和傳入changeLang變換語系
        console.log('useLang Object', langObj);
        dispatch(setLanguage(langObj.currentLanguage.toLowerCase()));
        dispatch(setCurrency(langObj.currentCurrency));
        changeLang(i18n, langObj.currentLanguage);
    }, [dispatch, langObj, i18n]);

    useEffect(() => {
        dispatch(setETID(ETID));
        if (!URLFOREIGN) console.log('URL Foreign data not found');
        URLFOREIGN && dispatch(setForeign(URLFOREIGN));
        dispatch(setIsGDS(isGDSURL));
    }, [dispatch, ETID, isGDSURL]);

    // 分類高鐵單程&來回程資料
    useEffect(() => {
        if (hasThsr && THSRInitData) {
            const oneWayInfo = getTHSRTripInfo(THSRInitData?.ThsrTripInfos, TTripType.oneway);
            const roundInfo = getTHSRTripInfo(THSRInitData?.ThsrTripInfos, TTripType.round);
            //存入state
            setThsrOneWayTripInfo(oneWayInfo);
            roundInfo && setThsrRoundTripInfo(roundInfo);
        }
    }, [hasThsr, THSRInitData]);

    // 確認面板現在的項目數
    useEffect(() => {
        if (
            (!isGDSURL && !isNormalURL) ||
            (isGDSURL && !GListData) ||
            (isNormalURL && !ListData) ||
            (hasThsr && !THSRInitData) ||
            (THSRInitData && !thsrOneWayTripInfo && !thsrRoundTripInfo) // 防止處理分類資料過慢
        )
            return;
        const format = isGDSURL ? GListData.Format : ListData.Format;
        const status = {
            onewayQty: thsrOneWayTripInfo ? 1 : 0,
            roundQty: thsrRoundTripInfo ? 1 : 0,
            originQty: checkDefaultID(format).length, // 新版API(ProductItemList)Format裡的項目數就是Default的面板數
        };

        // if (isNormalURL) {
        //     status.originQty = checkDefaultID(format).length;
        // }
        // if (isGDSURL) {
        //     // TODO:之後調整成自建架構
        //     status.originQty = format.length;
        // }
        dispatch(setListStatus(status));
    }, [
        dispatch,
        isGDSURL,
        isNormalURL,
        hasThsr,
        GListData,
        ListData,
        THSRInitData,
        thsrOneWayTripInfo,
        thsrRoundTripInfo,
    ]);

    // 渲染面板
    const render = () => {
        // GDS
        if (isGDSURL) {
            if (isFailFetchGDSList || THSRIsInitFail) {
                return <div>{t('common.noListFormat')}</div>;
            }
            if (!GListData || hasThsr ? !THSRInitData : false) {
                // return <Loading icon={<LoadingIcon />} text={t('common.loading_project')} />;
                return <LoadingGradient />;
            }
            if (!hasThsr) {
                // 無設定高鐵直接Return原始方案面板
                return (
                    GListData && (
                        <div className="planlist">
                            <GdsListPanel isCrossell={isCrossell} />
                        </div>
                    )
                );
            }
            return (
                <div className="planlist">
                    {THSRInitData && thsrOneWayTripInfo && GListData ? (
                        <>
                            {
                                <NormalRailPanel
                                    THSRTripType={TTripType.oneway}
                                    THSRTripInfo={thsrOneWayTripInfo}
                                    isTHSRPanelOpen={THSROpenedPanel === TTripType.oneway}
                                    listIndex={-2}
                                    isCrossell={isCrossell}
                                >
                                    {THSROpenedPanel === TTripType.oneway && (
                                        <TGdsListPanel isCrossell={isCrossell} />
                                    )}
                                </NormalRailPanel>
                            }
                            {thsrRoundTripInfo && (
                                <NormalRailPanel
                                    THSRTripType={TTripType.round}
                                    THSRTripInfo={thsrRoundTripInfo}
                                    isTHSRPanelOpen={THSROpenedPanel === TTripType.round}
                                    listIndex={-1}
                                    isCrossell={isCrossell}
                                >
                                    {THSROpenedPanel === TTripType.round && (
                                        <TGdsListPanel isCrossell={isCrossell} />
                                    )}
                                </NormalRailPanel>
                            )}
                        </>
                    ) : null}
                    {/* {GListData && !isAllMustTHSR(GListData.Format) && <GdsListPanel />} */}
                    {GListData && <GdsListPanel isCrossell={isCrossell} />}
                </div>
            );
        }
        // Normal
        if (isNormalURL) {
            if (isFailFetchList || THSRIsInitFail) {
                return <div>{t('common.noListFormat')}</div>;
            }
            if (!ListData || hasThsr ? !THSRInitData : false) {
                // return <Loading icon={<LoadingIcon />} text={t('common.loading_project')} />;
                return <LoadingGradient />;
            }
            if (!hasThsr) {
                // 無設定高鐵直接Return原始方案面板
                return (
                    ListData && (
                        <div className="planlist">
                            <ListPanel isCrossell={isCrossell} />
                        </div>
                    )
                );
            }
            return (
                <div className="planlist">
                    {THSRInitData && thsrOneWayTripInfo && ListData ? (
                        <>
                            {
                                <NormalRailPanel
                                    THSRTripType={TTripType.oneway}
                                    THSRTripInfo={thsrOneWayTripInfo}
                                    isTHSRPanelOpen={THSROpenedPanel === TTripType.oneway}
                                    listIndex={-2}
                                    isCrossell={isCrossell}
                                >
                                    {THSROpenedPanel === TTripType.oneway && (
                                        <TListPanel isCrossell={isCrossell} />
                                    )}
                                </NormalRailPanel>
                            }
                            {thsrRoundTripInfo && (
                                <NormalRailPanel
                                    THSRTripType={TTripType.round}
                                    THSRTripInfo={thsrRoundTripInfo}
                                    isTHSRPanelOpen={THSROpenedPanel === TTripType.round}
                                    listIndex={-1}
                                    isCrossell={isCrossell}
                                >
                                    {THSROpenedPanel === TTripType.round && (
                                        <TListPanel isCrossell={isCrossell} />
                                    )}
                                </NormalRailPanel>
                            )}
                        </>
                    ) : null}
                    {/* {ListData && !isAllMustTHSR(ListData.Format) && <ListPanel />} */}
                    {ListData && <ListPanel isCrossell={isCrossell} />}
                </div>
            );
        }
        return <div>{t('common.noListFormat')}</div>;
    };

    return render();
};

const root = createRoot(document.getElementById('detailOptions')! as HTMLElement);
root.render(
    <React.StrictMode>
        <Provider store={store}>
            <Detail />
        </Provider>
    </React.StrictMode>
);

export default Detail;
